import React, {useState} from 'react';
import {styled, IconButton, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import Linkify from 'linkify-react';
import {MatrixEvent} from 'matrix-js-sdk';
import {useTranslation} from 'react-i18next';
import SenderName from './sender-name';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import {useMatrixContext} from '@/contexts/matrix/client';

const QuotedMessageContainer = styled('div')(({theme}) => ({
  position: 'relative',
  borderRadius: '18px',
  border: `3px solid ${theme.palette.primary.main}`,
  width: '80%',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.primary.light,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflowWrap: 'break-word'
}));

const CloseButton = styled(IconButton)(({theme}) => ({
  position: 'absolute',
  top: theme.spacing(0),
  right: theme.spacing(0),
  padding: '4px'
}));

interface QuotedMessageProps {
  message: MatrixEvent;
}

const QuotedMessage: React.FC<QuotedMessageProps> = ({message}) => {
  const [isVisible, setIsVisible] = useState(true);
  const {setMatrixEventToQuote} = useMatrixRoomContext();
  const {matrixUserId} = useMatrixContext();
  const {t} = useTranslation();
  const theme = useTheme();

  const memberId = matrixUserId;

  const onClose = () => {
    setIsVisible(false);
    setMatrixEventToQuote(null);
  };

  const effectiveMatrixEvent = message.getEffectiveEvent();

  const senderId = effectiveMatrixEvent.sender;
  const senderName =
    effectiveMatrixEvent.sender === memberId ? t('you') : message.event.sender;

  const messageText = effectiveMatrixEvent.content.body;
  const filteredMessageText = messageText.replace(
    /\[\[QUOTE.*?\]\](.*?)\[\[\/QUOTE\]\]/gs,
    ''
  );

  if (!isVisible) return null;

  return (
    <QuotedMessageContainer>
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      {typeof senderName === 'string' && (
        <SenderName senderId={senderId} senderName={senderName} />
      )}
      <ResponsiveTypography>
        <Linkify
          options={{
            target: '_blank',
            rel: 'noopener noreferrer',
            attributes: {
              style: {
                color: theme.palette.customColors?.urlLink
              }
            }
          }}
        >
          {filteredMessageText}
        </Linkify>
      </ResponsiveTypography>
    </QuotedMessageContainer>
  );
};

export default QuotedMessage;
